import { useCallback, useEffect, useState } from 'react'
import { AnticipationReconciliationController } from '../controllers/AnticipationReconciliationController'
import { IErrorResponse } from '../types/ErrorResponse'
import {
  IAnticipationReconciliationFilter,
  TAnticipationReconciliationDetail,
  TAnticipationReconciliationPagination,
} from '../types/AnticipationReconciliation'

const emptyPage: TAnticipationReconciliationPagination = {
  items: [],
  page: 0,
  rowsPerPage: 0,
  totalPages: 0,
  total: 0,
}

/** Lista. `enabled=false` não chama a API (só SysAdmin pode). */
export const useAnticipationReconciliations = ({
  enabled,
  ...filter
}: IAnticipationReconciliationFilter & { enabled: boolean }) => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(emptyPage)
  const [error, setError] = useState('')

  const { page, rows, search, startDate, endDate, products, affiliateds } =
    filter

  const execute = useCallback(async () => {
    if (!enabled) return
    setLoading(true)
    setError('')
    try {
      const response = await AnticipationReconciliationController.getAll({
        page,
        rows,
        search: search?.trim(),
        startDate,
        endDate,
        products,
        affiliateds,
      })
      if ((response as IErrorResponse).code) {
        setError((response as IErrorResponse).error)
        setData(emptyPage)
      } else {
        setData(response as TAnticipationReconciliationPagination)
      }
    } finally {
      setLoading(false)
    }
  }, [enabled, page, rows, startDate, endDate, products, affiliateds, search])

  // search fica fora: dispara só no Enter (mesmo comportamento das outras abas)
  useEffect(() => {
    execute()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, page, rows, startDate, endDate, products, affiliateds])

  return { loading, data, error, execute, clear: () => setError('') }
}

/** Detalhe por orderChargeId (id da cobrança). */
export const useAnticipationReconciliation = (orderChargeId?: string) => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<TAnticipationReconciliationDetail>()
  const [error, setError] = useState('')

  useEffect(() => {
    setData(undefined)
    setError('')
    if (!orderChargeId) return

    let active = true
    setLoading(true)
    AnticipationReconciliationController.get(orderChargeId)
      .then((response) => {
        if (!active) return
        if ((response as IErrorResponse).code) {
          setError((response as IErrorResponse).error)
        } else {
          setData(response as TAnticipationReconciliationDetail)
        }
      })
      .finally(() => active && setLoading(false))

    return () => {
      active = false
    }
  }, [orderChargeId])

  return { loading, data, error }
}
